import {
  calculateClassDuration,
  calculateClassTimes,
  isSubmittedOnTime,
} from '@/context/form/utils/dateTimeUtils';
import { ReportData } from '@/types/report';

// ===================================================================
// Diff form values กับ current state → ได้ before/after delta
// เก็บเฉพาะ field ที่เปลี่ยน (ไม่ใช่ full snapshot)
// ===================================================================

export interface EditDiff {
  before: Record<string, unknown>;
  after: Record<string, unknown>;
  hasChanges: boolean;
}

export function buildEditDiff<T extends Record<string, unknown>>(
  formShape: T,
  current: Partial<Record<keyof T, unknown>>
): EditDiff {
  const before: Record<string, unknown> = {};
  const after: Record<string, unknown> = {};

  for (const key of Object.keys(formShape)) {
    const formVal = formShape[key];
    const currentVal = current[key as keyof T];
    if (!isShallowEqual(formVal, currentVal)) {
      before[key] = currentVal ?? null;
      after[key] = formVal ?? null;
    }
  }

  return { before, after, hasChanges: Object.keys(after).length > 0 };
}

function isShallowEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    return a.every((v, i) => isShallowEqual(v, b[i]));
  }
  return false;
}

// ===================================================================
// Recompute derived fields (class_duration / start_date / end_date /
// submitted_on_time) ถ้า teaching_date / teaching_time_slot /
// teaching_duration อย่างใดอย่างหนึ่งเปลี่ยน
// ===================================================================

export function recomputeDerivedIfNeeded(
  after: Record<string, unknown>,
  current: Partial<ReportData>
): Record<string, unknown> {
  const teachingChanged =
    'teaching_date' in after ||
    'teaching_time_slot' in after ||
    'teaching_duration' in after;

  if (!teachingChanged) return after;

  const teachingDate = (after.teaching_date as string) ?? current.teaching_date;
  const teachingTimeSlot =
    (after.teaching_time_slot as string) ?? current.teaching_time_slot;
  const teachingDuration =
    (after.teaching_duration as string) ?? current.teaching_duration;

  if (!teachingDate || !teachingTimeSlot || !teachingDuration) return after;

  const classDuration = calculateClassDuration(teachingDuration);
  let startDate: string | null = null;
  let endDate: string | null = null;

  try {
    const times = calculateClassTimes(teachingDate, teachingTimeSlot, classDuration);
    startDate = times.startDate;
    endDate = times.endDate;
  } catch (e) {
    console.error('recomputeDerivedIfNeeded error:', e);
  }

  return {
    ...after,
    class_duration: classDuration,
    start_date: startDate,
    end_date: endDate,
    submitted_on_time: isSubmittedOnTime(endDate),
  };
}

// ===================================================================
// เติม before ให้ครอบคลุม key ที่เพิ่มเข้ามาตอน recompute
// (เพราะ derived ไม่ถูก diff ตรงๆ → ค่าเดิมไม่ได้อยู่ใน before)
// ===================================================================

export function fillBeforeForDerivedKeys(
  before: Record<string, unknown>,
  after: Record<string, unknown>,
  current: Partial<ReportData>
): Record<string, unknown> {
  const filled = { ...before };
  for (const key of Object.keys(after)) {
    if (!(key in filled)) {
      filled[key] = (current as Record<string, unknown>)[key] ?? null;
    }
  }
  return filled;
}

// ===================================================================
// Build development_report JSON string จาก sub-fields ตาม
// /form page3 — โครงเดียวกับ context/form/utils/reportPreparation.ts
// ===================================================================

export interface DevReportFormFields {
  learning_status: string;
  topic_learned: string;
  knowledge_rating: string;
  knowledge_behavior: string;
  skills_rating: string;
  skills_behavior: string;
  attitude_rating: string;
  attitude_behavior: string;
  student_improvement: string;
  problems_encountered: string[];
  problems_encountered_other: string;
  solutions_applied: string[];
  solutions_applied_other: string;
}

// ===================================================================
// Aggregate PENDING rows ของ report → ได้ค่า "latest per field"
// ใช้สำหรับ:
//  - Prefill form ของครู (effective current = DB + aggregated PENDING)
//  - หา PENDING ที่ถูก supersede ตอน submit ใหม่
// ===================================================================

export interface ReportChangeLog {
  id: string;
  report_id: string;
  type: 'TEACHER_REQUEST' | 'MANUAL';
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED';
  created_by: string;
  before: Record<string, unknown>;
  after: Record<string, unknown>;
  reason: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  created_at: string;
}

/**
 * รวม PENDING ทุก row → 1 jsonb ที่มี value ล่าสุดของแต่ละ field
 */
export function aggregatePendingValues(
  pendings: ReportChangeLog[]
): Record<string, unknown> {
  const latest: Record<string, { value: unknown; createdAt: string }> = {};
  for (const p of pendings) {
    for (const [field, value] of Object.entries(p.after)) {
      const existing = latest[field];
      if (!existing || p.created_at > existing.createdAt) {
        latest[field] = { value, createdAt: p.created_at };
      }
    }
  }
  const flat: Record<string, unknown> = {};
  for (const [field, { value }] of Object.entries(latest)) {
    flat[field] = value;
  }
  return flat;
}

/**
 * Merge report (DB) กับ aggregated pending → effective current
 */
export function mergeWithPending<T extends Record<string, unknown>>(
  report: T,
  aggregated: Record<string, unknown>
): T {
  return { ...report, ...aggregated };
}

/**
 * หา PENDING ids ที่ถูก supersede:
 *   keys(old.after) ⊆ keys(new.after)  →  old ถูกครอบหมด → CANCEL
 * (Cancel-on-supersede algorithm — ใช้กับ teacher submit)
 */
export function findSupersededPendingIds(
  pendings: ReportChangeLog[],
  newAfterKeys: string[]
): string[] {
  const keySet = new Set(newAfterKeys);
  return pendings
    .filter((p) => {
      const oldKeys = Object.keys(p.after);
      if (oldKeys.length === 0) return false;
      return oldKeys.every((k) => keySet.has(k));
    })
    .map((p) => p.id);
}

/**
 * หา PENDING ids ที่ overlap (มี field ทับซ้อน) กับ new submission
 *   keys(old.after) ∩ keys(new.after) ≠ ∅
 * (ใช้กับ admin MANUAL เพื่อเตือนว่ามี PENDING ของครูทับซ้อน)
 */
export function findOverlappingPendingIds(
  pendings: ReportChangeLog[],
  newAfterKeys: string[]
): string[] {
  const keySet = new Set(newAfterKeys);
  return pendings
    .filter((p) => Object.keys(p.after).some((k) => keySet.has(k)))
    .map((p) => p.id);
}

// ===================================================================
// Display helpers — แชร์ระหว่างหน้า admin queue และ dialog ประวัติแก้ไข
// ===================================================================

export const REPORT_FIELD_LABEL: Record<string, string> = {
  teaching_location: 'สถานที่สอน',
  teaching_type: 'ประเภทการสอน',
  teaching_date: 'วันที่สอน',
  teaching_time_slot: 'เวลาเริ่มสอน',
  teaching_duration: 'ระยะเวลา',
  student_id: 'นักเรียน (รหัส)',
  student_full_name: 'ชื่อ-นามสกุลนักเรียน',
  student_nickname: 'ชื่อเล่นนักเรียน',
  student_grade_level: 'ระดับชั้น',
  subject: 'วิชา',
  learning_status: 'สถานะการเรียน',
  teaching_content: 'หัวข้อ/เนื้อหา',
  development_report: 'รายงานพัฒนาการ',
  homework_assignment: 'การบ้าน',
  next_class_preparation: 'เตรียมเรียนคาบหน้า',
  worksheet_drive_link: 'ลิงก์ Drive',
  class_duration: 'class_duration (derived)',
  start_date: 'start_date (derived)',
  end_date: 'end_date (derived)',
  submitted_on_time: 'submitted_on_time (derived)',
  status: 'สถานะรายงาน',
  rejection_reason: 'เหตุผลที่ไม่อนุมัติ',
  note: 'หมายเหตุ',
  pinned: 'ปักหมุด',
};

export function labelOfReportField(field: string): string {
  return REPORT_FIELD_LABEL[field] ?? field;
}

export function formatReportFieldValue(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'boolean') return v ? 'ใช่' : 'ไม่ใช่';
  if (Array.isArray(v)) return v.length ? v.join(', ') : '—';
  if (typeof v === 'object') {
    try {
      return JSON.stringify(v);
    } catch {
      return String(v);
    }
  }
  const s = String(v);
  return s.length > 200 ? s.slice(0, 200) + '…' : s;
}

export function buildDevelopmentReportJson(form: DevReportFormFields): string {
  if (form.learning_status === 'น้องไม่เข้าเรียน (ลากะทันหัน)') {
    return 'นักเรียนไม่เข้าเรียน';
  }
  return JSON.stringify({
    topic_learned: form.topic_learned ?? '',
    knowledge_rating: form.knowledge_rating ?? '',
    knowledge_behavior: form.knowledge_behavior ?? '',
    skills_rating: form.skills_rating ?? '',
    skills_behavior: form.skills_behavior ?? '',
    attitude_rating: form.attitude_rating ?? '',
    attitude_behavior: form.attitude_behavior ?? '',
    student_improvement: form.student_improvement ?? '',
    problems_encountered: form.problems_encountered ?? [],
    problems_encountered_other: form.problems_encountered_other ?? '',
    solutions_applied: form.solutions_applied ?? [],
    solutions_applied_other: form.solutions_applied_other ?? '',
  });
}
